import React, { useContext, useEffect, useState } from 'react'
import { UserInfoContext } from '../../provider/UserInfoProvider';
import { UseConstant } from '../../libraries/UseConstant';
import { Box, ButtonGroup, Checkbox, SimpleGrid, Text, useColorModeValue } from '@chakra-ui/react';
import AddButton from '../../components/custom/AddButton';
import LinkButton from '../../components/custom/LinkButton';
import SearchBar from '../../components/search/SearchBar';
import Loading from '../../components/common/Loading';
import NoData from '../../components/common/NoData';
import Pagination from '../../components/common/Pagination';
import SingleProject from './SingleProject';
import PageHeader from '../../components/common/PageHeader';
import { GetDepartmentOptions, GetProjectTypeOptions, getTechnologyOptions, getUserOptions } from '../../components/options/SelectOptions';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../hooks/useToast';

export default function Projects() {
    const [showSearch, setShowSearch] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [technologies, setTechnologies] = useState([]);
    const [users, setUsers] = useState([]);
    const { constant } = UseConstant();
    const { PROJECT_LIST } = constant;

    const { userInfo } = useContext(UserInfoContext);
    const isAdmin = userInfo?.user_type === constant.USER_TYPE_ADMIN;

    // ページ番号
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    // 検索パラメータ
    const [searchParams, setSearchParams] = useState({});

    // 取得データ
    const [projects, setProjects] = useState([]);

    const { get, isLoading } = useApi();
    const { showError } = useToast();

    // 検索処理
    const getProjects = async () => {
        try {
            // 検索パラメータ
            let paging = {
                limit: PROJECT_LIST,
                offset: currentPageNo - 1
            };
            
            let params = { ...paging, ...searchParams };

            if(!isAdmin) {
                params = {
                    ...params,
                    user_id: userInfo?.id
                };
            }

            const url = isAdmin ? '/project' : '/my-project';

            const response = await get(url, params);

            if (response && response?.isSuccess === false) {
                showError('データの取得に失敗しました');
                return;
            }

            setProjects(response?.data?.projects || []);
            setTotalPages(Math.ceil(response?.data?.total / paging?.limit));
        } catch (error) {
            console.error('Error fetching projects:', error);
            showError('データの取得に失敗しました');
        }
    };

    const getBranchDepartmentCountryData = async () => {
        try {
            const response = await get('/common/branch-department-country-data', {
                department: true,
                technology: true
            });

            if (response.status !== "success") {
                showError('データの取得に失敗しました');
                return;
            }

            setDepartments(response.data?.departments || []);
            setTechnologies(response.data?.technologies || []);
        } catch (error) {
            // Silently fail for optional data
        }
    };

    const getUsersData = async () => {
        try {
            const response = await get('/common/user-data');

            if (response.status !== "success") {
                showError('データの取得に失敗しました');
                return;
            }

            setUsers(response.data?.users || []);
        } catch (error) {
            // Silently fail for optional data
        }
    };

    useEffect(() => {
        getBranchDepartmentCountryData();
        getUsersData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageNo]);

    const handleSearch = () => {
        setCurrentPageNo(1);
        getProjects();
    };

    const searchFields = [
        {
            name: 'search_project_title',
            label: 'プロジェクトタイトル',
            type: 'text',
        },
        {
            name: 'search_project_type',
            label: 'プロジェクトタイプ',
            type: 'dropdown',
            options: GetProjectTypeOptions(),
        },
        {
            name: 'search_department_id',
            label: '部署',
            type: 'dropdown',
            options: GetDepartmentOptions(departments),
        },
        ...(isAdmin ? [{
            name: 'search_user_id',
            label: '登録者',
            type: 'dropdown',
            options: getUserOptions(users),
        }] : []),
        {
            name: 'search_technology_ids',
            label: '技術スキル',
            type: 'checkboxes',
            options: getTechnologyOptions(technologies),
        }
    ];

    const handlePageChange = (page) => {
        setCurrentPageNo(page);
    }

    const CustomTable = () => {
        return (
            <SimpleGrid columns={{ base: 1, sm: 1, md: 1, lg: 3, '2xl': 4 }} spacing={6}>
                {projects.map((project, key) => (
                    <SingleProject 
                        key={key} 
                        project={project} 
                        searchedTechnologyIds={searchParams.search_technology_ids || []}
                    />
                ))}
            </SimpleGrid>
        )
    }

    return (
        <>
            <div className='admin-page-header'>
                <PageHeader headerTitle={"プロジェクト一覧"} />
                <ButtonGroup gap={2}>
                    {isAdmin 
                        ? <LinkButton buttonText={'戻る'} buttonUrl={'/admin-dashboard'} />
                        : 
                        (
                            <>
                                <AddButton buttonText={'新規登録'} buttonUrl={'/me/add-project'} />
                                <LinkButton buttonText={'ダッシュボード'} buttonUrl={'/user-dashboard'} />
                            </>
                        )
                    }
                </ButtonGroup>
            </div>

            {/* Add SearchBar component */}
            <Checkbox
                isChecked={showSearch}
                onChange={(e) => setShowSearch(e.target.checked)}
                colorScheme="orange"
                mb={4}
            >
                <Text fontSize="md" color={useColorModeValue('gray.800', 'white')}>
                    フィルターを表示
                </Text>
            </Checkbox>

            <Box className='admin-box'>
                {/* Search */}
                {showSearch && (
                    <Box mb={6}>
                        <SearchBar
                            searchFields={searchFields}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onSearch={handleSearch}
                        />
                    </Box>
                )}

                {isLoading
                    ? <Loading />
                    : projects.length === 0
                        ? <NoData />
                        : <CustomTable />
                }
                <Pagination
                    currentPage={currentPageNo}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </Box>
        </>
    )
}
